/**
 * One toast in the notifications viewport: its kind's icon and border, the
 * title, an optional description and a close button.
 */

import * as ToastPrimitives from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, X, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { LoadingSpinner } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { ToastData, ToastType } from './toast.types';

interface ToastLook {
  icon: typeof CheckCircle2 | null;
  border: string;
  iconBg: string;
  iconColor: string;
}

const LOOKS: Record<ToastType, ToastLook> = {
  default: { icon: null, border: 'border-border', iconBg: 'bg-muted', iconColor: 'text-muted-foreground' },
  loading: { icon: null, border: 'border-primary/60', iconBg: 'bg-primary/10', iconColor: 'text-primary' },
  success: { icon: CheckCircle2, border: 'border-success/60', iconBg: 'bg-success/10', iconColor: 'text-success' },
  error: { icon: XCircle, border: 'border-destructive/60', iconBg: 'bg-destructive/10', iconColor: 'text-destructive' },
  warning: { icon: AlertTriangle, border: 'border-warning/60', iconBg: 'bg-warning/10', iconColor: 'text-warning' },
};

const ROOT =
  'group pointer-events-auto relative flex w-full max-w-[360px] items-start gap-3 overflow-hidden rounded-lg border bg-card px-4 py-3 pr-9 shadow-lg transition-all data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0';

function ToastIcon({ type, look }: { type: ToastType; look: ToastLook }) {
  const Icon = look.icon;
  if (type === 'loading')
    return (
      <div className={cn('mt-0.5 flex shrink-0 items-center justify-center rounded-full p-1', look.iconBg, look.iconColor)}>
        <LoadingSpinner size="xs" />
      </div>
    );
  if (!Icon) return null;
  return (
    <div className={cn('mt-0.5 shrink-0 rounded-full p-1', look.iconBg, look.iconColor)}>
      <Icon className="h-4 w-4" aria-hidden />
    </div>
  );
}

export function ToastItem({ toast, onClosed }: { toast: ToastData; onClosed: (id: string) => void }) {
  const { t } = useTranslation('common');
  const look = LOOKS[toast.type] ?? LOOKS.default;
  return (
    <ToastPrimitives.Root
      duration={toast.persistent ? Number.POSITIVE_INFINITY : 5000}
      className={cn(ROOT, look.border)}
      onOpenChange={(open) => !open && onClosed(toast.id)}
    >
      <ToastIcon type={toast.type} look={look} />
      <div className="grid min-w-0 flex-1 gap-0.5">
        <ToastPrimitives.Title className="text-sm font-semibold leading-tight text-foreground">{toast.title}</ToastPrimitives.Title>
        {toast.description ? <ToastPrimitives.Description className="text-xs leading-snug text-muted-foreground">{toast.description}</ToastPrimitives.Description> : null}
      </div>
      <ToastPrimitives.Close
        aria-label={t('aria.close')}
        className="absolute right-2 top-2 rounded p-1.5 text-muted-foreground opacity-60 transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </ToastPrimitives.Close>
    </ToastPrimitives.Root>
  );
}
